// Pure helpers for the fast PPM checklist. Every check defaults to Pass, so a normal
// visit needs no taps; the engineer only flags the exceptions and confirms. These build
// the completion-payload results, filter the checklist to the lift's drive type, group it
// into sections, and guard a Fail with no note. Pure (no React) so they unit test directly.
import type { ChecklistItemDef, ChecklistResult, ChecklistStatus, DriveType } from "./offline/types";

export function buildChecklistResults(
  items: ChecklistItemDef[],
  statuses: Record<string, ChecklistStatus>,
  notes: Record<string, string>,
): ChecklistResult[] {
  return items.map((i) => {
    const status = statuses[i.id] ?? "pass";
    // A note only belongs to a fail/advisory (the only statuses that show the input), so
    // a note left over from a status the engineer cycled away from is never submitted.
    const note = status === "fail" || status === "advisory" ? notes[i.id]?.trim() : undefined;
    return { id: i.id, item: i.description, category: i.category, status, ...(note ? { note } : {}) };
  });
}

// The canonical maintenance-checklist section order (mirrors the server MAINTENANCE_SECTIONS),
// used to order the sections on the PWA.
export const MAINTENANCE_SECTIONS = [
  "Machine room",
  "Top of car",
  "Lift shaft and pit",
  "Lift car",
  "General",
  // The ELE-MS-001 Section 3 Periodic Maintenance Schedule tasks, grouped by cadence.
  "Monthly maintenance",
  "Quarterly maintenance",
  "Six-monthly maintenance",
  "Insurance and LOLER compliance",
] as const;

// Does a checklist item apply to a lift's drive type? B (or untagged) always applies; H
// only to a hydraulic (or unknown "both") lift; E only to an electric traction (or "both")
// lift. Mirrors the server itemAppliesToDriveType. Pure.
export function itemAppliesToDriveType(applies: "B" | "H" | "E" | undefined, driveType: DriveType): boolean {
  if (!applies || applies === "B") return true;
  if (applies === "H") return driveType === "hydraulic" || driveType === "both";
  return driveType === "electric_traction" || driveType === "both"; // applies === "E"
}

// Filter the checklist to the items the engineer should see for a lift's drive type. A null
// drive type (e.g. a call-out, or a cached job from before driveType shipped) leaves the
// list unchanged. Pure.
export function filterChecklistByDriveType(items: ChecklistItemDef[], driveType: DriveType | null): ChecklistItemDef[] {
  if (!driveType) return items;
  return items.filter((i) => itemAppliesToDriveType(i.applies, driveType));
}

export interface ChecklistGroup {
  key: string; // the section name, or the category, used as a React key
  label: string;
  items: ChecklistItemDef[];
}

// Group checklist items for display. When the items carry sections (the comprehensive
// maintenance checklist), group by section in the canonical order, with any unknown section
// appended. Otherwise group by category (the thin per-lift-type templates). Pure.
export function groupChecklistItems(
  items: ChecklistItemDef[],
  categoryLabel: (category: string) => string,
  categoryOrder: readonly string[],
): ChecklistGroup[] {
  const hasSections = items.some((i) => i.section);
  if (hasSections) {
    const buckets = new Map<string, ChecklistItemDef[]>();
    for (const it of items) {
      const key = it.section ?? "Other";
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(it);
    }
    const ordered = [...MAINTENANCE_SECTIONS, ...buckets.keys()];
    const seen = new Set<string>();
    const groups: ChecklistGroup[] = [];
    for (const key of ordered) {
      if (seen.has(key) || !buckets.has(key)) continue;
      seen.add(key);
      groups.push({ key, label: key, items: buckets.get(key)! });
    }
    return groups;
  }
  // Category grouping (thin templates with no sections).
  const order = [...categoryOrder, "__other__"];
  const buckets = new Map<string, ChecklistItemDef[]>();
  for (const it of items) {
    const cat = categoryOrder.includes(it.category) ? it.category : "__other__";
    if (!buckets.has(cat)) buckets.set(cat, []);
    buckets.get(cat)!.push(it);
  }
  return order
    .filter((cat) => buckets.has(cat))
    .map((cat) => ({ key: cat, label: cat === "__other__" ? "Other" : categoryLabel(cat), items: buckets.get(cat)! }));
}

// The first item the engineer marked Fail but left without a note, or null if every Fail
// has a note. A Fail needs a note (what is the fault), so the PWA blocks completion until
// one is added. Advisory notes stay optional. Pure.
export function firstFailMissingNote(
  items: ChecklistItemDef[],
  statuses: Record<string, ChecklistStatus>,
  notes: Record<string, string>,
): ChecklistItemDef | null {
  for (const i of items) {
    if ((statuses[i.id] ?? "pass") === "fail" && !(notes[i.id]?.trim())) return i;
  }
  return null;
}
